import React from 'react';
import { Link } from 'react-router-dom';
import DiscussionList from '../components/DiscussionList';
import { useAuth } from '../context/AuthContext';
import NwsAlertsPanel from '../components/NwsAlertsPanel';
import { RolePermission } from '../types';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const can = (permission: RolePermission) => !!user?.permissions?.includes(permission);
  const canCompose = can('create_discussions');
  const canViewDraftQueue = can('view_unpublished_discussions');
  const canAccessStaffOps = can('access_staff_operations');
  const canReviewReports = can('manage_weather_reports');
  const canInvite = can('invite_users');
  const canManageUsers = can('view_users');
  const canManagePermissions = user?.role === 'owner';

  return (
    <div className="page-shell">
      <div className="container">
        <div className="hero-card">
          <div>
            <p className="eyebrow">Dashboard</p>
            <h2 className="page-heading">GLSN Ops Dashboard</h2>
            <p className="page-copy">This dashboard keeps SPC-style internal discussions at the center with severe weather reports and core staff operations alongside them.</p>
          </div>
          <div className="hero-actions">
            {canCompose ? (
              <>
                <Link to="/compose" className="btn btn-primary">Create internal discussion</Link>
                {canAccessStaffOps && <Link to="/staff/ops-center" className="btn btn-secondary">Open ops center</Link>}
                {canViewDraftQueue && <Link to="/staff/drafts" className="btn btn-secondary">Open draft queue</Link>}
              </>
            ) : (
              <>
                <Link to="/archive" className="btn btn-secondary">Browse archive</Link>
                <Link to="/register" className="btn btn-primary">Create member account</Link>
              </>
            )}
          </div>
        </div>

        <div className="dashboard-grid">
          <section className="module-card module-card-primary">
            <div className="module-header">
              <div>
                <p className="eyebrow">Core Product</p>
                <h3 className="section-heading">Internal Discussions</h3>
              </div>
              <span className="module-status module-status-live">Live</span>
            </div>
            <p className="page-copy">Create, publish, print, tag, and search operational discussion products modeled after SPC workflows.</p>
            <div className="module-actions">
              {canCompose && <Link to="/compose" className="btn btn-primary">Create discussion</Link>}
              <Link to="/search" className="btn btn-secondary">Search products</Link>
              <Link to="/archive" className="btn btn-secondary">Public archive</Link>
            </div>
          </section>


          <section className="module-card">
            <div className="module-header">
              <div>
                <p className="eyebrow">Public</p>
                <h3 className="section-heading">Severe Weather Reports</h3>
              </div>
              <span className="module-status module-status-live">Live</span>
            </div>
            <p className="page-copy">Anyone can send severe weather reports with required attachments so staff can review tornado, wind, hail, and other damage evidence.</p>
            <div className="module-actions">
              <Link to="/report" className="btn btn-secondary">Submit report</Link>
              {canReviewReports && <Link to="/staff/reports" className="btn btn-secondary">Review inbox</Link>}
            </div>
          </section>

          <section className="module-card">
            <div className="module-header">
              <div>
                <p className="eyebrow">People</p>
                <h3 className="section-heading">User Operations</h3>
              </div>
              <span className={`module-status ${canManageUsers ? 'module-status-live' : 'module-status-limited'}`}>{canManageUsers ? 'Owner access' : 'Restricted'}</span>
            </div>
            <p className="page-copy">Manage user accounts and role assignments using the capabilities granted to your role.</p>
            <div className="module-actions">
              {canManageUsers ? <Link to="/manage-users" className="btn btn-secondary">Manage users</Link> : <span className="module-note">Owner-only controls</span>}
            </div>
          </section>

          {canManagePermissions && <section className="module-card">
            <div className="module-header">
              <div>
                <p className="eyebrow">Owner</p>
                <h3 className="section-heading">Role permissions</h3>
              </div>
              <span className="module-status module-status-live">Owner access</span>
            </div>
            <p className="page-copy">Configure discussion, operations, and administration capabilities for each role.</p>
            <div className="module-actions">
              <Link to="/manage-permissions" className="btn btn-secondary">Configure permissions</Link>
            </div>
          </section>}

          <section className="module-card">
            <div className="module-header">
              <div>
                <p className="eyebrow">Onboarding</p>
                <h3 className="section-heading">Invite Links</h3>
              </div>
              <span className={`module-status ${canInvite ? 'module-status-live' : 'module-status-limited'}`}>{canInvite ? 'Live' : 'Restricted'}</span>
            </div>
            <p className="page-copy">Authorized roles can issue invite links while public users can still self-register as members.</p>
            <div className="module-actions">
              {canInvite ? <Link to="/invite-users" className="btn btn-secondary">Invite users</Link> : <span className="module-note">Invite permission required</span>}
            </div>
          </section>

          <section className="module-card">
            <div className="module-header">
              <div>
                <p className="eyebrow">Staff tools</p>
                <h3 className="section-heading">Ops center</h3>
              </div>
              <span className={`module-status ${canAccessStaffOps ? 'module-status-live' : 'module-status-limited'}`}>{canAccessStaffOps ? 'Live' : 'Restricted'}</span>
            </div>
            <p className="page-copy">Staff can access the central operations center for discussion drafting, report review, and active-event coordination.</p>
            <div className="module-actions">
              {canAccessStaffOps ? <Link to="/staff/ops-center" className="btn btn-secondary">Open ops center</Link> : <span className="module-note">Staff and higher access</span>}
            </div>
          </section>
        </div>

        <div className="page-section">
          <div className="section-heading-row">
            <h3 className="section-heading">Published discussion feed</h3>
          </div>
          <DiscussionList status="published" />
        </div>

        <NwsAlertsPanel />
      </div>
    </div>
  );
};

export default HomePage;
